/**
 * notify — unified wrapper that fires a sonner toast AND pushes a
 * record into the notification centre store.
 *
 * Prefer `notify.success(...)` over raw `toast.success(...)` so the
 * event lands in the bell history. Toasts are transient; the store is
 * durable and persists across reloads.
 */

import { toast, type ExternalToast } from 'sonner'
import { useNotifications, type NotificationTone } from '@/store/notifications'

interface NotifyOptions {
  description?: string
  href?: string
  source?: string
  /** Suppress the toast; still record in the notification centre. */
  silent?: boolean
  /** Passthrough to sonner for duration / action / etc. */
  toast?: ExternalToast
}

function dispatch(tone: NotificationTone, title: string, opts: NotifyOptions = {}) {
  const { description, href, source, silent, toast: toastOpts } = opts

  // 1. Push to the store (durable)
  useNotifications.getState().push({ tone, title, description, href, source })

  // 2. Fire transient toast (unless silent)
  if (!silent) {
    const fn =
      tone === 'success'
        ? toast.success
        : tone === 'warn'
          ? toast.warning
          : tone === 'danger'
            ? toast.error
            : toast.info
    fn(title, { description, ...toastOpts })
  }
}

export const notify = {
  success: (title: string, opts?: NotifyOptions) => dispatch('success', title, opts),
  info: (title: string, opts?: NotifyOptions) => dispatch('info', title, opts),
  warn: (title: string, opts?: NotifyOptions) => dispatch('warn', title, opts),
  error: (title: string, opts?: NotifyOptions) => dispatch('danger', title, opts),
}
